import { Button } from '@mui/material'
import { useMemo } from 'react'

import { IColumnsType } from 'pared/components/basicUi/table'

import { IPrepItemOnHandTableData } from './usePrepItemOnHandDataSource'

interface IUsePrepItemOnHandColumnsProps {
  onEditClick: (record: IPrepItemOnHandTableData) => void
  dataSource: IPrepItemOnHandTableData[]
}

const usePrepItemOnHandColumns = ({
  onEditClick,
  dataSource,
}: IUsePrepItemOnHandColumnsProps): IColumnsType<IPrepItemOnHandTableData>[] => {
  return useMemo(() => {
    // Find the maximum number of count types across all items
    const maxCountTypes = Math.max(
      ...dataSource.map((item) => item.countTypes?.length || 0),
      0,
    )

    // Base columns
    const baseColumns: IColumnsType<IPrepItemOnHandTableData>[] = [
      {
        key: 'edit',
        title: 'Edit',
        minWidth: '80px',
        align: 'center',
        render: (_, record) => {
          if (!record) return null
          return (
            <Button
              variant="text"
              size="small"
              onClick={() => onEditClick(record)}
              sx={{
                textTransform: 'none',
                fontFamily: 'Lexend-Regular',
                fontSize: '0.875rem',
              }}
            >
              Edit
            </Button>
          )
        },
      },
      {
        key: 'prepItem',
        title: 'Prep Item',
        minWidth: '150px',
        align: 'left',
      },
    ]

    // Dynamically generate count type columns
    const countTypeColumns: IColumnsType<IPrepItemOnHandTableData>[] = []
    for (let i = 0; i < maxCountTypes; i++) {
      const index = i + 1
      countTypeColumns.push(
        {
          key: `countType${index}`,
          title: `Count Type ${index}`,
          minWidth: '120px',
          align: 'center',
          render: (_, record) => {
            if (!record) return ''
            return record.countTypes?.[i]?.batchType || ''
          },
        },
        {
          key: `countType${index}PerStandardUnit`,
          title: `Count Type ${index} per Standard Unit`,
          minWidth: '200px',
          align: 'center',
          render: (_, record) => {
            if (!record) return ''
            const value = record.countTypes?.[i]?.standardUnitsPerBatch
            if (value === undefined || value === null) return ''
            return value
          },
        },
      )
    }

    return [...baseColumns, ...countTypeColumns]
  }, [onEditClick, dataSource])
}

export default usePrepItemOnHandColumns
